import React, { useCallback, useContext, useMemo } from "react";
import { View } from "@common/types/views/views";
import { AccountWithTransactions } from "@common/dto/reports/get-account-transactions-report-response.dto";
import { AccountTransactionsLine } from "@common/types/domains/accounting/accountTransactions";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { GeneralLedgerView } from "@common/types/views/generalLedgerView";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewConfig } from "@lib/views";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { journalEntryOpenPath } from "@components/routes/productEntityOpenPaths";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useSelectedLedger } from "@hooks/useSelectedLedger";
import { useEntitiesById } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import { useSearchParams } from "react-router-dom";
import { AccountingReportContext } from "../AccountingReport.context";
import { useAccountingTabContext } from "../useAccountingTab";
import { buildGLRows } from "./glViewBuilder";
import {
    defaultGlTransactionsDrilldown,
    GlTransactionsDrilldownConfig,
} from "./glTransactionsDrilldown";
import { buildGLConfig } from "./glViewConfig";
import { useGlSourceDrilldown } from "./useGlSourceDrilldown";
import { GLTableMeta, GLViewRow } from "./glViewTypes";
import styles from "./GeneralLedgerTable.module.scss";

interface Props {
    data: AccountWithTransactions[];
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    view: GeneralLedgerView;
    onViewChange?: (view: GeneralLedgerView) => void;
    viewConfig?: ViewConfig<GLViewRow, string, never>;
    /**
     * When true, open the journal entry drawer overlay on the current page
     * instead of routing to the GL journal-entry URL. Used by GlDrilldownDrawer
     * so callers (invoice details, etc.) aren't navigated away.
     */
    openJournalEntriesInPlace?: boolean;
    transactionsDrilldown?: GlTransactionsDrilldownConfig;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
}

/**
 * General Ledger table rendered via the shared ViewTable runtime.
 * - Columns are fixed: Date, Source, Reference, Description, Counterparty, Split, Amount, Balance
 * - Amount and Balance are pinned right
 * - Each account row is expandable; children are opening balance, transactions, closing balance
 * - Expand/collapse is controlled via `expanded` / `onExpandedChange`
 */
export const GeneralLedgerTable: React.FC<Props> = ({
    data,
    expanded,
    onExpandedChange,
    view,
    onViewChange,
    viewConfig,
    openJournalEntriesInPlace = false,
    transactionsDrilldown = defaultGlTransactionsDrilldown,
    emptyView,
    footer,
}) => {
    const { filters } = useAccountingTabContext();
    const { numberFormat } = useContext(AccountingReportContext);
    const { openOverlay } = useOverlays();
    const navigate = useStateAwareNavigate();
    const { workspaceId } = useScope();
    const [searchParams] = useSearchParams();
    const entitiesById = useEntitiesById();
    const { selectedLedger } = useSelectedLedger(
        filters.entityId,
        filters.ledgerId,
    );
    const selectedLedgerBasis =
        selectedLedger?.basis ?? AccountingLedgerBasis.cash;

    const isClassesEnabled = useIsClassesEnabled();

    const onSourceClick = useGlSourceDrilldown({
        basis: selectedLedgerBasis,
        transactionsDrilldown,
    });

    const onReferenceClick = useCallback(
        (transaction: AccountTransactionsLine) => {
            if (openJournalEntriesInPlace) {
                const entity = entitiesById.get(transaction.entityId);
                if (!entity) {
                    return;
                }
                openOverlay("journalEntryDrawer", {
                    entity,
                    journalEntryId: transaction.referenceNumber,
                    ledgerBasis: selectedLedgerBasis,
                    ledgerId: filters.ledgerId,
                });
                return;
            }

            navigate(
                journalEntryOpenPath({
                    entityId: transaction.entityId,
                    journalEntryId: transaction.referenceNumber,
                    ledgerBasis: selectedLedgerBasis,
                    ledgerId: filters.ledgerId,
                    workspaceId: workspaceId ?? undefined,
                    searchParams,
                }),
            );
        },
        [
            entitiesById,
            filters.ledgerId,
            navigate,
            openJournalEntriesInPlace,
            openOverlay,
            searchParams,
            selectedLedgerBasis,
            workspaceId,
        ],
    );

    // Whole-row navigation to the underlying journal entry, so the link
    // doesn't depend on the Reference column being visible (PRO-8850).
    // Cells with their own click behavior (Source, Reference) capture
    // clicks and are unaffected.
    const onRowClick = useCallback(
        (row?: GLViewRow) => {
            if (!row || row.rowKind !== "transaction" || !row.transaction) {
                return;
            }
            onReferenceClick(row.transaction);
        },
        [onReferenceClick],
    );

    const tableMeta: GLTableMeta = useMemo(
        () => ({ callbacks: { onSourceClick, onReferenceClick } }),
        [onSourceClick, onReferenceClick],
    );

    const rows = useMemo(() => buildGLRows(data), [data]);

    const defaultGlViewConfig = useMemo(
        () =>
            buildGLConfig({
                multiEntity: false,
                numberFormat,
                isClassesEnabled,
            }),
        [numberFormat, isClassesEnabled],
    );

    const handleViewChange = useMemo(
        () =>
            onViewChange
                ? (newView: View) => onViewChange(newView as GeneralLedgerView)
                : undefined,
        [onViewChange],
    );

    return (
        <ViewTable
            data={rows}
            view={view}
            config={viewConfig ?? defaultGlViewConfig}
            className={styles.generalLedgerTable}
            tableMeta={tableMeta}
            onRowClick={onRowClick}
            rowBorders={false}
            enableColumnResizing
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            onViewChange={handleViewChange}
            nestingIndentPx={16}
            fixedTopOffsetPx
            emptyView={emptyView}
            footer={footer}
        />
    );
};
